import { DonationStatus, DonationType, PostStatus, SearchMeditatorActions } from "@/constants";
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";

export const createApi = (axiosInstance: AxiosInstance) => {

    return _thenData({

        // auth
        login: (data: LoginRequest): Promise<JWT> => axiosInstance.post(`/auth/admin/login`, data),

        refreshToken: (refreshToken: string): Promise<JWT> => axiosInstance.get(`/auth/user/refreshToken`, {
            params: {
                refreshToken
            }
        }),

        checkPhoneNumber: (data: { phone: string }): Promise<{ requestId: number, phone: string }> => axiosInstance.post(`/auth/admin/checkPhoneNumber`, data),

        verifyPhoneNumber: (data: VerifyPhoneNumberRequest): Promise<{
            status: boolean;
            requestId: number;
            jwt: string;
            expiration: string
        }> => axiosInstance.post(`/users/verifyPhoneNumberOrEmail`, data),

        createNewPassword: (password: string): Promise<MessageResponse> => axiosInstance.put(`/users/createNewPassword`, { password }),

        // levels
        getLevels: (): Promise<Level[]> => axiosInstance.get(`/levels`),

        // meditation retreat
        getMeditationRetreats: (page: number, year?: number, limit: number = 10): Promise<PaginatedResponse<MeditationRetreat>> => axiosInstance.get(`/meditationEvents/all`, {
            params: {
                year,
                page: page - 1, // Adjusting for 0-based index
                limit
            }
        }),

        getMeditationRetreatDetail: (id: string): Promise<MeditationRetreat> => axiosInstance.get(`/meditationEvents/${id}`),

        createMeditationRetreat: (data: CreateMeditationRetreatRequest): Promise<MeditationRetreat> => axiosInstance.post(`/meditationEvents`, data),

        updateMeditationRetreat: (id: number, data: Partial<CreateMeditationRetreatRequest>): Promise<MeditationRetreat> => axiosInstance.put(`/meditationEvents/${id}`, data),

        deleteMeditationRetreat: (id: number): Promise<MessageResponse> => axiosInstance.delete(`/meditationEvents/${id}`),

        getMeditationRetreatByLevel: (page: number, levelIds: number[], limit: number = 10): Promise<PaginatedResponse<MeditationRetreat>> => axiosInstance.get(`/meditationEvents`, {
            params: {
                levels: levelIds.join(','),
                page: page - 1, // Adjusting for 0-based index
                limit
            }
        }),

        // contacts
        getContacts: (): Promise<Contact[]> => axiosInstance.get(`/contacts`),

        createContact: (data: CreateContactRequest): Promise<Contact> => axiosInstance.post(`/contacts`, data),

        updateContact: (id: number, data: Partial<CreateContactRequest>): Promise<Contact> => axiosInstance.put(`/contacts/${id}`, data),

        deleteContacts: (data: { ids: number[] }): Promise<void> => axiosInstance.delete(`/contacts/delete`, {
            data
        }),

        // posts
        getPosts: (page: number): Promise<PaginatedResponse<Post>> => axiosInstance.get(`/posts`, {
            params: {
                page: page - 1, // Adjusting for 0-based index
                limit: 5
            }
        }),

        createPost: (status: PostStatus, data: FormData): Promise<Post> => axiosInstance.post(`/posts`, data, {
            params: {
                status
            },
            timeout: 30000 // 30 seconds timeout for creation
        }),

        updatePost: (id: number, status: PostStatus, data: FormData): Promise<Post> => axiosInstance.put(`/posts/${id}`, data, {
            params: {
                status
            },
            timeout: 30000 // 30 seconds timeout for update
        }),

        deletePosts: (data: { ids: number[] }): Promise<void> => axiosInstance.delete(`/posts/delete`, {
            data,
            timeout: 30000 // 30 seconds timeout for deletion
        }),

        publishPost: (id: number): Promise<Post> => axiosInstance.put(`/posts/${id}/publish`),

        // meditators
        getAllMeditators: (page: number, limit: number = 10): Promise<PaginatedResponse<Meditator>> => axiosInstance.get(`/yogi`, {
            params: {
                page: page - 1,
                limit
            }
        }),

        getMeditatorById: (id: string): Promise<MeditatorWithMeditationRetreat> => axiosInstance.get(`/yogi/${id}`, {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            }
        }),

        addNewMeditator: (data: AddNewMeditatorRequest): Promise<MeditatorWithMeditationRetreat> => axiosInstance.post(`/yogi`, data),

        updateMeditator: ({ id, data }: { id: number, data: Partial<AddNewMeditatorRequest> }): Promise<MeditatorWithMeditationRetreat> => axiosInstance.put(`/yogi/${id}`, data),

        deleteUser: (id: number): Promise<MessageResponse> => axiosInstance.delete(`/users/${id}`),

        manualRegisterMeditator: (data: ManualRegisterMeditatorRequest): Promise<MeditatorWithMeditationRetreat> => axiosInstance.post(`/yogi/manualRegister`, data),

        passMeditatorsToRetreat: (retreatId: number, data: FormData): Promise<MessageResponse> => axiosInstance.post(`/yogi/meditationEvents/${retreatId}/pass`, data, {
            timeout: 60000, //
            timeoutErrorMessage: 'Please wait and try again!'
        }),

        failMeditatorsToRetreat: (retreatId: number, data: FormData): Promise<MessageResponse> => axiosInstance.post(`/yogi/meditationEvents/${retreatId}/fail`, data, {
            timeout: 60000, // 1min
            timeoutErrorMessage: 'Please wait and try again!'
        }),

        // meditator by retreat
        getMeditatorsByMeditationRetreat: ({ retreatId, page, status = "ACCEPTED", limit = 10 }: { retreatId: string, page: number, status?: "ACCEPTED" | "PENDING", limit?: number }): Promise<PaginatedResponse<Meditator>> => axiosInstance.get(`/yogi/meditationEvents/${retreatId}`, {
            params: {
                page: page - 1, // Adjusting for 0-based index
                limit,
                status
            }
        }),

        // registered meditators
        getRegistrationPendingMeditators: (page: number, limit: number = 10): Promise<PaginatedResponse<MeditatorWithMeditationRetreat>> => axiosInstance.get(`/yogi/registerPending`, {
            params: {
                page: page - 1, // Adjusting for 0-based index
                limit
            }
        }),

        getMeditatorWithRetreat: (id: number): Promise<MeditatorWithMeditationRetreat> => axiosInstance.get(`/yogi/${id}`),

        acceptRegisteredMeditator: (ids: number[]): Promise<MessageResponse> => axiosInstance.post(`/yogi/accept`, {
            ids
        }),

        rejectRegisteredMeditator: (ids: number[]): Promise<MessageResponse> => axiosInstance.post(`/yogi/reject`, {
            ids
        }),

        // cancellation request meditators
        getCancellationRequestedMeditators: (page: number): Promise<PaginatedResponse<MeditatorWithMeditationRetreat>> => axiosInstance.get(`/yogi/declinePending`, {
            params: {
                page: page - 1, // Adjusting for 0-based index
            }
        }),

        confirmCancellationRequest: (ids: number[]): Promise<MessageResponse> => axiosInstance.post(`/yogi/declinedConfirm`, { ids }),

        rejectCancellationRequest: (ids: number[]): Promise<MessageResponse> => axiosInstance.post(`/yogi/declinedReject`, { ids }),

        manualRequestCancellation: (retreatId: number, data: Partial<Meditator>): Promise<MeditatorWithMeditationRetreat> => axiosInstance.post(`/yogi/meditationEvents/${retreatId}/manual?action=decline`, data),

        // search meditators
        searchMeditators: (query: string): Promise<Meditator[]> => axiosInstance.get(`/search/yogi`, {
            params: {
                k: query,
            }
        }),

        searchMeditatorByPhoneNumber: (keyword: string, action: SearchMeditatorActions): Promise<MeditatorWithMeditationRetreat> => axiosInstance.get(`/search`, {
            params: {
                keyword,
                action
            }
        }),

        searchMeditatorByKeywordAndEvent: (keyword: string, eventId: number): Promise<Meditator[]> => axiosInstance.get(`/search/yogi`, {
            params: {
                k: keyword,
                eventId
            }
        }),

        // donors
        getDonations: ({ retreatId, page, type, limit = 10, status = DonationStatus.ACCEPTED }: { retreatId?: number, page: number, type: DonationType, limit?: number, status?: DonationStatus }): Promise<PaginatedResponse<Donation>> => axiosInstance.get(`/donations`, {
            params: {
                eventId: type === DonationType.BOHI_ETERNAL_TREE ? null : retreatId,
                type,
                page: page - 1, // Adjusting for 0-based index
                limit,
                status
            }
        }),

        updateDonationAmount: (id: number, amount: number): Promise<Donation> => axiosInstance.put(`/donations/${id}`, { amount }),

        deleteDonationRequest: (id: number): Promise<MessageResponse> => axiosInstance.delete(`/donations/${id}`),

        // payments
        getAllPayments: (): Promise<Payment[]> => axiosInstance.get(`/payments`),

        addNewPayment: (data: FormData): Promise<Payment> => axiosInstance.post(`/payments`, data, {
            timeout: 30000 // 30 seconds timeout for creation
        }),

        updatePayment: ({ id, data }: { id: number, data: FormData }): Promise<Payment> => axiosInstance.put(`/payments/${id}`, data, {
            timeout: 30000 // 30 seconds timeout for update
        }),

        deletePayment: (id: number): Promise<MessageResponse> => axiosInstance.delete(`/payments/${id}`),

        // statistics
        getStatisticsData: (): Promise<Statistics[]> => axiosInstance.get(`/statistics`),

        // noti
        sendNotification: (data: SendNotificationRequest): Promise<MessageResponse> => axiosInstance.post(`/notifications/send`, data),

        announceRoomSelection: (retreatId: number) => axiosInstance.post(`meditationEvents/${retreatId}/sendNotiToYogi`),

        // create admin account
        createAdminAccount: (data: CreateAdminAccountRequest): Promise<MessageResponse> => axiosInstance.post(`/auth/admin/create`, data),

        // user list
        getUserList: (page: number, limit: number): Promise<PaginatedResponse<Meditator>> => axiosInstance.get(`/users`, {
            params: {
                page: page - 1, // Adjusting for 0-based index
                limit
            }
        }),

        // excel exports
        exportMeditatorsByRetreat: (retreatId: number): Promise<Blob> => axiosInstance.get(`/excel/meditationEvents/${retreatId}/yogi/export`, {
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
            responseType: 'blob'
        }),

        exportDonorsByMeditationRetreat: (retreatId: number, type: DonationType = DonationType.EVENT_ALM): Promise<Blob> => axiosInstance.get(`/excel/meditationEvents/${retreatId}/donors/export?type=${type}`, {
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
            responseType: 'blob'
        }),

        // check In/Out
        getMeditationRetreatAttendance: (retreatId: number, page: number, limit: number = 10): Promise<PaginatedResponse<Meditator>> => axiosInstance.get(`/yogi/meditationEvents/${retreatId}`, {
            params: {
                status: "ACCEPTED",
                page: page - 1,
                limit
            }
        }),

        checkInCheckOutMeditator: (meditatorId: number): Promise<MeditatorWithMeditationRetreat> => axiosInstance.post(`/yogi/clockIn`, {
            id: meditatorId
        }),

        // room
        getRooms: (): Promise<Room[]> => axiosInstance.get(`/rooms`),

        getRoomDetail: (id: number): Promise<Room> => axiosInstance.get(`/rooms/${id}`),

        createNewRoom: (data: CreateRoomRequest[]): Promise<Room[]> => axiosInstance.post(`/rooms`, data),

        updateRoom: (id: number, data: Partial<CreateRoomRequest>): Promise<Room> => axiosInstance.put(`/rooms/${id}`, data),

        deleteRoom: (id: number) => axiosInstance.delete(`/rooms/${id}`),

        getPendingRoomRequest: (roomId: number): Promise<RoomRequestUser[]> => axiosInstance.get(`/rooms/${roomId}/pending-yogi`),

        getAcceptedMeditatorByRoomId: (roomId: number): Promise<RoomRequestUser[]> => axiosInstance.get(`/rooms/${roomId}/accepted-yogi`),

        acceptRoomRequest: (roomId: number, userId: number) => axiosInstance.post(`/rooms/${roomId}/approved`, { id: userId }),

        rejectRoomRequest: (roomId: number, userId: number) => axiosInstance.post(`/rooms/${roomId}/rejected`, { id: userId }),

        getWaitingMeditators: (): Promise<RoomRequestUser[]> => axiosInstance.get(`/waitingRooms/yogi`),

        changeMeditatorRoom: (roomId: number, userId: number) => axiosInstance.post(`/rooms/${roomId}/moveRoom`, { id: userId }),

        getRoomsByMeditationEvent: (meditationEventId: number): Promise<Room[]> => axiosInstance.get(`/meditationEvents/${meditationEventId}/rooms`),

        moveMeditatorToWaitingRoom: (roomId: number, userId: number): Promise<{ success: boolean, message: string }> => axiosInstance.post(`/rooms/${roomId}/moveWaitingRoom`, { id: userId }),

        assignMeditatorToRoom: (roomId: number, userId: number): Promise<{ success: boolean, message: string }> => axiosInstance.post(`/rooms/${roomId}/assigned-yogis`, {
            id: [userId]
        }),

        getRoomUnassignedMeditators: (retreatId: number): Promise<Meditator[]> => axiosInstance.get(`/meditationEvents/${retreatId}/unassigned-yogis`),

        assignRoomsToMeditationRetreat: (retreatId: number, roomIds: number[]): Promise<MeditationRetreat> => axiosInstance.post(`/meditationEvents/${retreatId}/setRooms`, { ids: roomIds }),
    })

};

type ErrorResponse = { error?: string };

function _thenData<T>(OriginApi: T): T {
    const _API: Partial<T> = {};
    for (const key in OriginApi) {
        if (Object.prototype.hasOwnProperty.call(OriginApi, key)) {
            _API[key] = ((...args: unknown[]) =>
                (OriginApi[key] as (...args: unknown[]) => Promise<unknown>)(...args)
                    .then((res => (res as AxiosResponse).data))
                    .catch((error: AxiosError<ErrorResponse>) => {
                        throw ((error.response?.data) as ErrorWithMessage)?.message
                        || error.response?.data?.error
                        || error.message
                        || 'An unknown error occurred';
                    })
            ) as T[typeof key];
        }
    }
    return _API as T;
}

export function setApiToken({
    axiosInstance,
    token,
}: {
    axiosInstance: AxiosInstance;
    token: string;
}) {
    if (token) {
        axios.defaults.headers['Authorization'] = `Bearer ${token}`;
        axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
        delete axiosInstance.defaults.headers.common['Authorization'];
    }
}